/**
 * Document cropper modal: shows the photo with 4 draggable corner handles
 * (auto-detected by Gemini), lets the user fine-tune the quadrilateral, and
 * returns the warped (rectified) JPEG ready to be sent to OCR + saved as
 * the expense attachment.
 *
 * Flow:
 *   1. parent passes the picked File -> we POST it to
 *      /api/expense-receipts/detect-corners
 *   2. backend stashes the original, calls Gemini, returns {token, corners}
 *   3. user drags the 4 SVG handles overlayed on the image preview
 *   4. on confirm, we POST {token, corners} to /api/expense-receipts/warp
 *      and bubble the resulting JPEG up via onConfirm(blob).
 */
import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X, ArrowsClockwise, Check, SpinnerGap } from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const HANDLE_LABELS = ['Haut-gauche', 'Haut-droit', 'Bas-droit', 'Bas-gauche'];

const DocumentCropModal = ({ file, onCancel, onConfirm }) => {
  const [busy, setBusy] = useState(true);
  const [warping, setWarping] = useState(false);
  const [rotating, setRotating] = useState(false);
  const [imgUrl, setImgUrl] = useState('');
  const [imgSize, setImgSize] = useState({ w: 1, h: 1 });
  const [renderSize, setRenderSize] = useState({ w: 1, h: 1 });
  const [token, setToken] = useState('');
  const [ext, setExt] = useState('.jpg');
  const [corners, setCorners] = useState([
    { x: 0.05, y: 0.05 },
    { x: 0.95, y: 0.05 },
    { x: 0.95, y: 0.95 },
    { x: 0.05, y: 0.95 },
  ]);
  const dragRef = useRef({ idx: -1 });
  const containerRef = useRef(null);

  // Build preview url + send to backend for corner detection
  useEffect(() => {
    if (!file) return;
    const url = URL.createObjectURL(file);
    setImgUrl(url);
    // Probe dimensions locally so we can render the SVG overlay even before
    // the network call returns.
    const probe = new Image();
    probe.onload = () => setImgSize({ w: probe.naturalWidth, h: probe.naturalHeight });
    probe.src = url;

    const run = async () => {
      try {
        const fd = new FormData();
        fd.append('file', file);
        const r = await axios.post(`${API_URL}/api/expense-receipts/detect-corners`, fd, { withCredentials: true });
        if (r.data?.token) {
          setToken(r.data.token);
          setExt(r.data.ext || '.jpg');
        }
        if (Array.isArray(r.data?.corners) && r.data.corners.length === 4) {
          setCorners(r.data.corners.map(c => ({ x: Number(c.x) || 0, y: Number(c.y) || 0 })));
        }
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Detection auto echouee, ajuste les coins manuellement');
      } finally {
        setBusy(false);
      }
    };
    run();
    return () => URL.revokeObjectURL(url);
  }, [file]);

  // Recompute render size when image loads (CSS-rendered size in the modal)
  const onImgLoad = (e) => {
    const r = e.target.getBoundingClientRect();
    setRenderSize({ w: r.width, h: r.height });
  };
  useEffect(() => {
    const handler = () => {
      const img = containerRef.current?.querySelector('img');
      if (img) {
        const r = img.getBoundingClientRect();
        setRenderSize({ w: r.width, h: r.height });
      }
    };
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  // Drag logic — pointer events for mouse + touch
  const startDrag = (idx) => (e) => {
    e.preventDefault();
    dragRef.current.idx = idx;
    e.target.setPointerCapture?.(e.pointerId);
  };
  const moveDrag = (e) => {
    if (dragRef.current.idx < 0) return;
    const rect = containerRef.current.querySelector('img').getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;
    setCorners(prev => prev.map((c, i) => i === dragRef.current.idx ? { x: Math.max(0, Math.min(1, x)), y: Math.max(0, Math.min(1, y)) } : c));
  };
  const endDrag = () => { dragRef.current.idx = -1; };

  const handleRotate90 = async () => {
    if (!token || rotating) return;
    setRotating(true);
    try {
      const fd = new FormData();
      fd.append('token', token);
      fd.append('ext', ext);
      fd.append('direction', 'cw');
      const r = await axios.post(`${API_URL}/api/expense-receipts/rotate`, fd, {
        withCredentials: true,
      });
      if (Array.isArray(r.data?.corners) && r.data.corners.length === 4) {
        setCorners(r.data.corners.map(c => ({ x: Number(c.x) || 0, y: Number(c.y) || 0 })));
      }
      // Refetch the rotated image (single-use file endpoint) — easiest: fetch the file URL via the stash.
      // Since we don't expose the raw stashed file, we ask the server for it via a small GET on warp? No — simpler: re-encode locally by serving a 1-px-extra cache buster on a known stash url.
      // The cleanest solution is to send the rotated preview back from the rotate endpoint as a base64.
      // For now we keep the existing imgUrl but rotate it visually via CSS, then on next dimension probe we swap; corners are server-side correct.
      // To keep the preview accurate, we re-rotate the displayed image visually so it matches the server state.
      const img = new Image();
      img.onload = () => setImgSize({ w: img.naturalWidth, h: img.naturalHeight });
      img.src = imgUrl;
      // Swap displayed image to the rotated bytes (best UX) via canvas
      try {
        const baseImg = await new Promise((resolve, reject) => {
          const i = new Image();
          i.onload = () => resolve(i);
          i.onerror = reject;
          i.src = imgUrl;
        });
        const canvas = document.createElement('canvas');
        canvas.width = baseImg.naturalHeight;
        canvas.height = baseImg.naturalWidth;
        const ctx = canvas.getContext('2d');
        ctx.translate(canvas.width / 2, canvas.height / 2);
        ctx.rotate(Math.PI / 2);
        ctx.drawImage(baseImg, -baseImg.naturalWidth / 2, -baseImg.naturalHeight / 2);
        const newUrl = canvas.toDataURL('image/jpeg', 0.9);
        URL.revokeObjectURL(imgUrl);
        setImgUrl(newUrl);
      } catch { /* keep old preview */ }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Rotation impossible');
    } finally {
      setRotating(false);
    }
  };

  const handleReset = () => {
    setCorners([
      { x: 0.02, y: 0.02 },
      { x: 0.98, y: 0.02 },
      { x: 0.98, y: 0.98 },
      { x: 0.02, y: 0.98 },
    ]);
  };

  const handleConfirm = async () => {
    if (!token) {
      toast.error('Session expiree, reprenez la photo');
      onCancel();
      return;
    }
    setWarping(true);
    try {
      const fd = new FormData();
      fd.append('token', token);
      fd.append('ext', ext);
      fd.append('corners', JSON.stringify(corners));
      const r = await axios.post(`${API_URL}/api/expense-receipts/warp`, fd, {
        withCredentials: true,
        responseType: 'blob',
      });
      // Wrap into a File object so the parent can treat it like the original picker output
      const warpedFile = new File([r.data], `scan_${Date.now()}.jpg`, { type: 'image/jpeg' });
      onConfirm(warpedFile);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Echec du recadrage');
    } finally {
      setWarping(false);
    }
  };

  // SVG polygon points (use pixel coordinates of the rendered image)
  const polyPoints = corners.map(c => `${c.x * renderSize.w},${c.y * renderSize.h}`).join(' ');

  return (
    <div className="form-modal-overlay" style={{ zIndex: 1200, alignItems: 'stretch' }}>
      <div className="form-modal-card" style={{ maxWidth: '640px', background: '#0F172A', color: 'white' }}>
        <div className="form-modal-header" style={{ borderBottom: '1px solid #1E293B', background: '#0F172A', color: 'white' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: '1rem', fontWeight: 700 }}>Recadrer le document</span>
            {busy && <SpinnerGap size={16} className="spin" />}
          </div>
          <button onClick={onCancel} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'white' }}>
            <X size={22} />
          </button>
        </div>

        <div
          ref={containerRef}
          className="form-modal-body"
          style={{ background: '#0F172A', padding: 14, display: 'flex', justifyContent: 'center', alignItems: 'flex-start' }}
          onPointerMove={moveDrag}
          onPointerUp={endDrag}
          onPointerCancel={endDrag}
        >
          {imgUrl && (
            <div style={{ position: 'relative', maxWidth: '100%', display: 'inline-block', touchAction: 'none' }}>
              <img
                src={imgUrl}
                alt="A recadrer"
                onLoad={onImgLoad}
                style={{ maxWidth: '100%', maxHeight: '62vh', display: 'block', borderRadius: 6, userSelect: 'none' }}
                draggable={false}
              />
              {renderSize.w > 1 && (
                <svg
                  width={renderSize.w}
                  height={renderSize.h}
                  style={{ position: 'absolute', top: 0, left: 0, pointerEvents: 'none' }}
                >
                  <polygon
                    points={polyPoints}
                    fill="rgba(59,130,246,0.15)"
                    stroke="#60A5FA"
                    strokeWidth={2.5}
                    strokeDasharray="6 4"
                  />
                </svg>
              )}
              {renderSize.w > 1 && corners.map((c, idx) => (
                <button
                  type="button"
                  key={idx}
                  onPointerDown={startDrag(idx)}
                  aria-label={HANDLE_LABELS[idx]}
                  data-testid={`crop-handle-${idx}`}
                  style={{
                    position: 'absolute',
                    left: c.x * renderSize.w - 16,
                    top: c.y * renderSize.h - 16,
                    width: 32, height: 32,
                    borderRadius: '50%',
                    background: 'rgba(59,130,246,0.95)',
                    border: '3px solid white',
                    boxShadow: '0 2px 6px rgba(0,0,0,0.5)',
                    cursor: 'grab',
                    touchAction: 'none',
                    padding: 0,
                  }}
                />
              ))}
            </div>
          )}
        </div>

        <div className="form-modal-actions" style={{ background: '#0F172A', borderTop: '1px solid #1E293B', flexWrap: 'wrap' }}>
          <button type="button" className="btn btn-secondary" onClick={handleReset} disabled={warping} style={{ flex: '1 1 100px' }} data-testid="crop-reset-btn">
            Tout
          </button>
          <button type="button" className="btn btn-secondary" onClick={handleRotate90} disabled={warping || rotating || busy} style={{ flex: '1 1 100px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6 }} data-testid="crop-rotate-btn">
            {rotating ? <SpinnerGap size={16} className="spin" /> : <ArrowsClockwise size={16} />} Pivoter
          </button>
          <button type="button" className="btn btn-secondary" onClick={onCancel} disabled={warping} style={{ flex: '1 1 100px' }} data-testid="crop-cancel-btn">
            Annuler
          </button>
          <button type="button" className="btn btn-primary" onClick={handleConfirm} disabled={busy || warping} style={{ flex: '2 1 160px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6 }} data-testid="crop-confirm-btn">
            {warping ? <SpinnerGap size={16} className="spin" /> : <Check size={16} weight="bold" />}
            {warping ? 'Traitement...' : 'Confirmer'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DocumentCropModal;
